import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { getMatchingProfiles } from '../utils/matching';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ProfileCard from '../components/ui/ProfileCard';
import FilterPanel from '../components/ui/FilterPanel';

const { FiUsers, FiTarget, FiFilter, FiSearch } = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();
  const { profiles, matches } = useData();
  const [activeTab, setActiveTab] = useState('matches');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    platforms: [],
    genres: []
  });
  const [showFilters, setShowFilters] = useState(false);

  const currentUserProfile = profiles.find(p => p.userId === user.id);
  const matchingProfiles = currentUserProfile 
    ? getMatchingProfiles(currentUserProfile, profiles.filter(p => p.approved), matches)
    : [];

  const filteredProfiles = profiles
    .filter(p => p.approved && p.userId !== user.id)
    .filter(p => {
      if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !p.company?.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (filters.role && p.role !== filters.role) {
        return false;
      }
      if (filters.platforms.length > 0 && 
          !filters.platforms.some(platform => p.platforms?.includes(platform))) {
        return false;
      }
      if (filters.genres.length > 0 && 
          !filters.genres.some(genre => p.genres?.includes(genre))) {
        return false;
      }
      return true;
    });

  if (!currentUserProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Not Found</h2>
          <p className="text-gray-600 mb-6">Please complete your profile to access the dashboard.</p>
          <button
            onClick={() => window.location.href = '#/profile-wizard'}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Complete Profile
          </button>
        </div>
      </div>
    );
  }

  if (!currentUserProfile.approved) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Under Review</h2>
          <p className="text-gray-600">Your profile is being reviewed by our admin team. You'll be notified once it's approved.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {currentUserProfile.name}!
          </h1>
          <p className="text-gray-600">
            Discover new connections and grow your game business.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTarget} className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Suggested Matches</p>
                <p className="text-2xl font-bold text-gray-900">{matchingProfiles.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Profiles</p>
                <p className="text-2xl font-bold text-gray-900">{profiles.filter(p => p.approved).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiFilter} className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Your Role</p>
                <p className="text-lg font-semibold text-gray-900">{currentUserProfile.role}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
          <button
            onClick={() => setActiveTab('matches')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'matches'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Suggested Matches ({matchingProfiles.length})
          </button>
          <button
            onClick={() => setActiveTab('directory')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'directory'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Directory ({filteredProfiles.length})
          </button>
        </div>

        {/* Search and Filters */}
        {activeTab === 'directory' && (
          <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiFilter} className="w-4 h-4" />
                <span>Filters</span>
              </button>
            </div>
            
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-4 pt-4 border-t border-gray-200"
              >
                <FilterPanel filters={filters} setFilters={setFilters} />
              </motion.div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeTab === 'matches' 
            ? matchingProfiles.map(profile => (
                <ProfileCard key={profile.id} profile={profile} showMatchScore />
              ))
            : filteredProfiles.map(profile => (
                <ProfileCard key={profile.id} profile={profile} />
              ))
          }
        </div>

        {/* Empty States */}
        {activeTab === 'matches' && matchingProfiles.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiTarget} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No matches yet</h3>
            <p className="text-gray-600">
              We're working on finding the perfect matches for you. Check back soon!
            </p>
          </div>
        )}

        {activeTab === 'directory' && filteredProfiles.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiUsers} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No profiles found</h3>
            <p className="text-gray-600">
              Try adjusting your search terms or filters to find more profiles.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;